import React, { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import { Page, pdfjs } from 'react-pdf';
import { RenderMode } from 'react-pdf/dist/cjs/shared/types';
import classnames from 'classnames';
import OverlayLoader from 'Components/UIComponents/UIOverlayLoader';
import { useIsMobile, useIsTablet, useToggler } from 'Hooks/Common';
import { useInteractMedia } from 'Hooks/Document';
import interact from 'interactjs';

import MobileRenderer from './MobileRenderer';

import 'react-pdf/dist/esm/Page/AnnotationLayer.css';

export interface DocumentPageProps {
  pageNumber: number;
  scale?: number;
  style?: CSSProperties;
  className?: string;
  pageClassName?: string;
  onTap?: (event) => void;
  onDrop?: (event) => void;
  onLoad?: () => void;
  renderAnnotationLayer?: boolean;
  enableCustomMobileRenderer?: boolean;
}

const DocumentPage = ({
  style,
  pageNumber,
  onTap,
  onDrop,
  onLoad,
  className,
  pageClassName,
  scale,
  renderAnnotationLayer = true,
  enableCustomMobileRenderer = true,
}: DocumentPageProps) => {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isExtraMobile = useInteractMedia(['xs']);
  const [renderMode, setRenderMode] = useState<RenderMode>(
    (isMobile && enableCustomMobileRenderer) || (isTablet && enableCustomMobileRenderer)
      ? 'custom'
      : 'canvas',
  );
  const [isLoading, toggleIsLoading] = useToggler(true);
  const pageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let interactable;

    if (pageRef.current)
      interactable = interact(pageRef.current)
        .dropzone({
          enabled: true,
          accept: '.draggable',
          overlap: 1,
          ondrop: onDrop,
        })
        .on('tap', event => {
          onTap && onTap(event);
        });

    return () => {
      if (interactable) interactable.unset();
    };
  }, [pageRef, onTap, onDrop]);

  const handleLoad = useCallback(
    async page => {
      if (enableCustomMobileRenderer) {
        const operatorList = await page.getOperatorList();

        const isSomeImage = operatorList.fnArray.some(
          fn => fn === pdfjs.OPS.paintImageXObject,
        );

        if (isSomeImage) {
          setRenderMode('canvas');
        }
      }

      toggleIsLoading();
      if (onLoad) onLoad();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enableCustomMobileRenderer, onLoad],
  );

  return (
    <div className={classnames('documentPage', className)} style={style} ref={pageRef}>
      <div className={classnames('documentPage__inner', pageClassName)}>
        {isLoading && <OverlayLoader />}
        <Page
          className="documentPage__inner-pdf_page"
          pageNumber={pageNumber}
          pageIndex={pageNumber}
          scale={scale}
          onLoadSuccess={handleLoad}
          renderAnnotationLayer={renderAnnotationLayer}
          renderTextLayer={false}
          renderMode={renderMode}
          customRenderer={
            isExtraMobile || isMobile || isTablet ? MobileRenderer : undefined
          }
        />
      </div>
    </div>
  );
};

export default DocumentPage;
